import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';

import { DATE_FORMAT } from 'appConstants';

import '@fontsource/inter/400';
import '@fontsource/inter/700';
import { useLocalization } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TCustomerDetails } from './i18n/TModules';

export interface ICustomerIdentityHeaderProps {
  data: {
    id?: number | string;
    customer_id?: string | number | null;
    alternate_id?: string | number | null;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    joined_at?: string | null;
    state?: string;
  };
  actions?: React.ReactNode;
  /* The smaller Figma scale. Both customer tabs now pass it, so the 52 default below is currently
     unreached - it is kept as the measured Frame B value, not as a live branch. PLA-8844. */
  compactName?: boolean;
}

/* Frame B geometry, figma-spec 8.2 / 6.11, converted once here so no rule below restates a number.
   The band is drawn at 273,142 in frame space and every child is quoted absolutely, so each offset
   is the child minus the band. Frame B is the LIGHT surface (8.3): white at radius 5, text 0A0A0A —
   NOT the dark Customer 360 tokens. */
const BAND = { width: 1227, height: 158, radius: 5, paddingLeft: 20, paddingRight: 15 };
// Name 162, pills 193, field labels 246, values 262, CTA 250 — all minus the band top of 142.
const TOP = { name: 20, pills: 51, label: 104, value: 120, cta: 108 };
// Field columns 293 / 370 / 470 / 581 minus the band left of 273.
const FIELD_LEFT = [20, 97, 197, 308];
// Each column may run to 9px short of the next one; the last takes the 105.28 the frame draws it at.
const FIELD_MAX_WIDTH = [68, 91, 102, 130];
// Name box ends at 624 against pill 1 at 639; pill 1 ends at 704.2 against pill 2 at 714.
const NAME_TO_PILL_GAP = 15;
/*
  Two name scales, both off Figma p5eywtYuRIGlMvjJVNWxCu: the detail band draws 52 (`5290:16826`),
  Customer 360 heads its page at Bold 36 (`5290:17362`). The spec states line-height 32.8 there,
  shorter than the glyph box - it holds in Figma but clips under `overflow: hidden` in CSS, so it is
  scaled from the band's own ratio. PLA-8844.
*/
const NAME_SCALE = {
  default: { size: 52, lineHeight: 66.4, tracking: -0.52 },
  compact: { size: 36, lineHeight: 46, tracking: 0 }
};
const PILL_HEIGHT = 20.8;
/* Design values, nodeId 5290:16977. Literals rather than tokens because this band renders inside
   two different theme scopes - DarkPageScope on the detail page, IntelligenceThemeScope on Customer
   360 - and neither scope's custom properties resolve in the other. PLA-8844. */
const TEXT = '#F5F7FB';
const LABEL = '#AEBBD0';
const SURFACE = '#101830';
const PILL_ACTIVE = 'rgba(30, 185, 128, 0.3)';
const PILL_ACTIVE_DOT = '#1eb980';
/* The frame draws the ACTIVE pill only, so the inactive treatment is derived, not read: the same
   shape in the secondary grey the legacy profile already uses for muted customer text. */
const PILL_INACTIVE = 'rgba(155, 155, 155, 0.3)';
const PILL_INACTIVE_DOT = '#9b9b9b';
/* Frame B is drawn in Inter (8.2), and every size below was metered against its glyphs - fall
   through to the dashboard stack and the 52/66.4/-0.52 name is a different box. The face is the one
   the Intelligence route already self-hosts; the fallback is what this page rendered before it. */
const FONT_STACK =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export const Band = styled.section`
  position: relative;
  box-sizing: border-box;
  max-width: 100%;
  height: ${BAND.height}px;
  margin-bottom: 20px;
  border-radius: ${BAND.radius}px;
  background: ${SURFACE};
  box-shadow: none;
  font-family: ${FONT_STACK};
  overflow: hidden;
`;

const NameRow = styled.div`
  position: absolute;
  top: ${TOP.name}px;
  left: ${BAND.paddingLeft}px;
  right: ${BAND.paddingRight}px;
  display: flex;
  align-items: flex-start;
`;

export const Name = styled.h1<{ $compact?: boolean }>`
  margin: 0;
  max-width: 60%;
  overflow: hidden;
  color: ${TEXT};
  font-size: ${props => (props.$compact ? NAME_SCALE.compact : NAME_SCALE.default).size}px;
  font-weight: 700;
  line-height: ${props => (props.$compact ? NAME_SCALE.compact : NAME_SCALE.default).lineHeight}px;
  letter-spacing: ${props => (props.$compact ? NAME_SCALE.compact : NAME_SCALE.default).tracking}px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/*
  On the band the pills do not sit on the name's centre line: the frame puts them at 193 against a
  name box starting at 162, so that offset is stated rather than derived. It is measured against the
  52px name box, so it cannot carry over to the smaller Customer 360 heading - there the pills are
  centred on the name's line box instead. PLA-8844.
*/
const Pills = styled.div<{ $compact?: boolean }>`
  display: flex;
  margin-top: ${props =>
    props.$compact ? (NAME_SCALE.compact.lineHeight - PILL_HEIGHT) / 2 : TOP.pills - TOP.name}px;
  margin-left: ${NAME_TO_PILL_GAP}px;
  margin-right: 10px;
`;

export const Pill = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: ${PILL_HEIGHT}px;
  padding: 0 10px;
  border-radius: 100px;
  background: ${({ $active }) => ($active ? PILL_ACTIVE : PILL_INACTIVE)};
  color: ${TEXT};
  font-size: 11px;
  line-height: ${PILL_HEIGHT}px;
  white-space: nowrap;

  & + & {
    margin-left: 10px;
  }

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    margin-right: 5px;
    border-radius: 50%;
    background: ${({ $active }) => ($active ? PILL_ACTIVE_DOT : PILL_INACTIVE_DOT)};
  }
`;

const Field = styled.div<{ $left: number; $maxWidth: number }>`
  position: absolute;
  top: ${TOP.label}px;
  left: ${({ $left }) => $left}px;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
`;

export const FieldLabel = styled.div`
  color: ${LABEL};
  font-size: 10px;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const FieldValue = styled.div`
  margin-top: ${TOP.value - TOP.label - 12}px;
  overflow: hidden;
  color: ${LABEL};
  font-size: 13.5px;
  font-weight: 400;
  line-height: 17.6px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/* The existing Delete / Activate / Deactivate row. The frame pastes today's button as a bitmap at
   1316,148 (8.1, node 3583-16127) rather than redesigning it, so it keeps its shipped treatment and
   only its position inside the band is taken from the frame. */
const ActionSlot = styled.div`
  position: absolute;
  top: 20px;
  right: ${BAND.paddingRight - 4}px;
`;

const EMPTY_VALUE = '-';

/**
 * The minimum identity band on the Customer detail page — name, status, the four identifiers and
 * the Customer 360 action (FR US-03, figma-spec 8.2 Frame B).
 *
 * It renders only for operators who can reach Customer 360; everyone else keeps the legacy profile.
 * See the deviation note in `CustomerReview`. PLA-8828.
 */
const CustomerIdentityHeader: React.FC<ICustomerIdentityHeaderProps> = ({
  data,
  actions,
  compactName
}) => {
  const { t } = useMultiLanguageTranslation();
  const { timezone } = useLocalization();

  const { id, customer_id, alternate_id, full_name, first_name, last_name, joined_at, state } =
    data;

  const name = useMemo(() => {
    const composed = [first_name, last_name].filter(Boolean).join(' ').trim();

    return full_name || composed || (id === undefined || id === null ? '' : `${id}`);
  }, [first_name, full_name, id, last_name]);

  const joined = useMemo(
    () =>
      joined_at ? moment.tz(`${joined_at}`.trim(), timezone).format(DATE_FORMAT) : EMPTY_VALUE,
    [joined_at, timezone]
  );

  const fields = useMemo(
    () => [
      { key: 'perx-id', label: t(`${TCustomerDetails}.PerxId`), value: id },
      { key: 'identifier', label: t(`${TCustomerDetails}.Identifier`), value: customer_id },
      { key: 'alternate-id', label: t(`${TCustomerDetails}.AlternateId`), value: alternate_id },
      { key: 'joined', label: t(`${TCustomerDetails}.Joined`), value: joined }
    ],
    [alternate_id, customer_id, id, joined, t]
  );

  const isActive = state === 'active';

  return (
    <Band data-testid="customer-identity-header">
      <NameRow>
        <Name data-testid="customer-identity-name" title={name} $compact={compactName}>
          {name}
        </Name>
        <Pills $compact={compactName}>
          <Pill $active={isActive} data-testid="customer-identity-status">
            {isActive
              ? t(`${TCustomerDetails}.StatusActive`)
              : t(`${TCustomerDetails}.StatusInactive`)}
          </Pill>
        </Pills>
      </NameRow>

      {fields.map((field, index) => {
        const text =
          field.value === undefined || field.value === null || field.value === ''
            ? EMPTY_VALUE
            : `${field.value}`;

        return (
          <Field key={field.key} $left={FIELD_LEFT[index]} $maxWidth={FIELD_MAX_WIDTH[index]}>
            <FieldLabel data-testid={`customer-identity-${field.key}-label`}>
              {field.label}
            </FieldLabel>
            <FieldValue data-testid={`customer-identity-${field.key}`} title={text}>
              {text}
            </FieldValue>
          </Field>
        );
      })}

      {actions ? <ActionSlot>{actions}</ActionSlot> : null}
    </Band>
  );
};

export default CustomerIdentityHeader;
